'use client'

import { useEffect, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import {
  Clock,
  Reply,
  ArrowUpRight,
  ArrowDownLeft,
  MessageSquare,
  Check,
  CheckCheck,
  X,
  Smartphone,
  RefreshCw,
  Timer,
  CheckCircle2,
  Search,
  MessageCircle,
  Loader2,
} from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import api from '../../lib/api'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { sendSmsSchema, type SendSmsFormData } from '../../../lib/schemas'
import { useMutation } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Badge } from '../ui/badge'
import { devicesQueryOptions } from '@/lib/queries'
import DevicePicker from './DevicePicker'
import { notify } from '@/lib/notify'

interface SMSMessage {
  _id: string
  sender?: string
  recipient?: string
  recipients?: string[]
  message: string
  status?: string
  channel?: 'sms' | 'whatsapp'
  requestedAt?: string
  sentAt?: string
  deliveredAt?: string
  readAt?: string
  failedAt?: string
  receivedAt?: string
  createdAt: string
  device?: {
    _id: string
    appVersionCode?: number
  }
  smsBatch?: string
  errorCode?: string
  errorMessage?: string
  error?: string
}

interface Device {
  id: string
  brand: string
  model: string
  enabled: boolean
  appVersionCode?: number
  displayName?: string
}

function ReplyDialog({ sms, onClose }: { sms: SMSMessage; onClose?: () => void }) {
  const [open, setOpen] = useState(false)

  const showToast = (title: string, description?: string, variant: 'default' | 'destructive' = 'default') => {
    if (variant === 'destructive') {
      notify.error(title, description)
    } else {
      notify.success(title, description)
    }
  }

  const {
    mutate: sendSms,
    isPending: isSendingSms,
    error: sendSmsError,
    isSuccess: isSendSmsSuccess,
  } = useMutation({
    mutationKey: ['send-sms'],
    mutationFn: (data: SendSmsFormData) => {
      // Transform frontend format to backend format
      const backendData = {
        deviceId: data.deviceId,
        recipient: data.recipients[0], // Take first recipient from array
        message: data.message
      }
      return api.post(`/gateway/devices/${data.deviceId}/send-sms`, backendData)
    },
    onSuccess: () => {
      showToast('Reply sent successfully!')
      setTimeout(() => {
        setOpen(false)
        if (onClose) onClose()
      }, 1500)
    },
    onError: (error: any) => {
      showToast('Error sending reply', error.response?.data?.message || error.message, 'destructive')
    },
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SendSmsFormData>({
    resolver: zodResolver(sendSmsSchema),
    defaultValues: {
      deviceId: sms?.device?._id,
      recipients: [sms.sender],
      message: '',
    },
  })

  const { data: devices } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  useEffect(() => {
    if (open) {
      reset({
        deviceId: sms?.device?._id,
        recipients: [sms.sender],
        message: '',
      })
    }
  }, [open, sms, reset])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant='ghost' size='sm' className='gap-1'>
          <Reply className='h-3.5 w-3.5' />
          Reply
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <MessageSquare className='h-5 w-5' />
            Reply to {sms.sender}
          </DialogTitle>
          <DialogDescription>
            Send a reply message to this sender
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) => sendSms(data))}
          className='space-y-4 mt-4'
        >
          <div className='space-y-4'>
            <div>
              <Controller
                name='deviceId'
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    defaultValue={sms?.device?._id}
                  >
                    <SelectTrigger aria-label='Send from'>
                      <SelectValue placeholder='Select a device' />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.isArray(devices) && devices.length > 0 ? (
                        devices.map((device: Device) => (
                          <SelectItem 
                            key={device.id} 
                            value={device.id}
                          >
                            {device.displayName || (device.brand && device.model ? `${device.brand} ${device.model}` : device.brand || device.model || 'Device')}{' '}
                            {device.enabled ? '' : '(disabled)'}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-devices" disabled>
                          No devices available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.deviceId && (
                <p className='text-sm text-destructive mt-1'>
                  {errors.deviceId.message}
                </p>
              )}
            </div>

            <div>
              <Input
                type='tel'
                placeholder='Phone Number'
                {...register('recipients.0')}
              />
              {errors.recipients?.[0] && (
                <p className='text-sm text-destructive mt-1'>
                  {errors.recipients[0].message}
                </p>
              )}
            </div>

            <div>
              <Textarea
                placeholder='Message'
                {...register('message')}
                rows={4}
              />
              {errors.message && (
                <p className='text-sm text-destructive mt-1'>
                  {errors.message.message}
                </p>
              )}
            </div>
          </div>
          {sendSmsError && (
            <div className='flex items-center gap-2 text-destructive'>
              <p>Error sending SMS: {sendSmsError.message}</p>
              <X className='h-5 w-5' />
            </div>
          )}

          {isSendSmsSuccess && (
            <div className='flex items-center gap-2 text-green-600'>
              <p>SMS sent successfully!</p>
              <Check className='h-5 w-5' />
            </div>
          )}

          <div className='flex justify-end gap-2'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSendingSms}>
              {isSendingSms && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              {isSendingSms ? 'Sending...' : 'Send Reply'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function FollowUpDialog({
  message,
  onClose,
}: {
  message: SMSMessage
  onClose?: () => void
}) {
  const [open, setOpen] = useState(false)

  const showToast = (title: string, description?: string, variant: 'default' | 'destructive' = 'default') => {
    if (variant === 'destructive') {
      notify.error(title, description)
    } else {
      notify.success(title, description)
    }
  }

  const {
    mutate: sendSms,
    isPending: isSendingSms,
    error: sendSmsError,
    isSuccess: isSendSmsSuccess,
  } = useMutation({
    mutationKey: ['send-sms'],
    mutationFn: (data: SendSmsFormData) => {
      // Transform frontend format to backend format
      const backendData = {
        deviceId: data.deviceId,
        recipient: data.recipients[0], // Take first recipient from array
        message: data.message
      }
      return api.post(`/gateway/devices/${data.deviceId}/send-sms`, backendData)
    },
    onSuccess: () => {
      showToast('Follow-up sent successfully!')
      setTimeout(() => {
        setOpen(false)
        if (onClose) onClose()
      }, 1500)
    },
    onError: (error: any) => {
      showToast('Error sending follow-up', error.response?.data?.message || error.message, 'destructive')
    },
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SendSmsFormData>({
    resolver: zodResolver(sendSmsSchema),
    defaultValues: {
      deviceId: message?.device?._id,
      recipients: [
        message.recipient ||
          (message.recipients && message.recipients[0]) ||
          '',
      ],
      message: '',
    },
  })

  const { data: devices } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  useEffect(() => {
    if (open) {
      reset({
        deviceId: message?.device?._id,
        recipients: [
          message.recipient ||
            (message.recipients && message.recipients[0]) ||
            '',
        ],
        message: '',
      })
    }
  }, [open, message, reset])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant='ghost' size='sm' className='gap-1'>
          <MessageSquare className='h-3.5 w-3.5' />
          Follow Up
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <MessageSquare className='h-5 w-5' />
            Follow Up with{' '}
            {message.recipient ||
              (message.recipients && message.recipients[0]) ||
              'Recipient'}
          </DialogTitle>
          <DialogDescription>
            Send a follow-up message to this recipient
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) => sendSms(data))}
          className='space-y-4 mt-4'
        >
          <div className='space-y-4'>
            <div>
              <Controller
                name='deviceId'
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    defaultValue={message?.device?._id}
                  >
                    <SelectTrigger aria-label='Send from'>
                      <SelectValue placeholder='Select a device' />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.isArray(devices) && devices.length > 0 ? (
                        devices.map((device: Device) => (
                          <SelectItem 
                            key={device.id} 
                            value={device.id}
                          >
                            {device.displayName || (device.brand && device.model ? `${device.brand} ${device.model}` : device.brand || device.model || 'Device')}{' '}
                            {device.enabled ? '' : '(disabled)'}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-devices" disabled>
                          No devices available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.deviceId && (
                <p className='text-sm text-destructive mt-1'>
                  {errors.deviceId.message}
                </p>
              )}
            </div>

            <div>
              <Input
                type='tel'
                placeholder='Phone Number'
                {...register('recipients.0')}
              />
              {errors.recipients?.[0] && (
                <p className='text-sm text-destructive mt-1'>
                  {errors.recipients[0].message}
                </p>
              )}
            </div>

            <div>
              <Textarea
                placeholder='Message'
                {...register('message')}
                rows={4}
              />
              {errors.message && (
                <p className='text-sm text-destructive mt-1'>
                  {errors.message.message}
                </p>
              )}
            </div>
          </div>
          {sendSmsError && (
            <div className='flex items-center gap-2 text-destructive'>
              <p>Error sending SMS: {sendSmsError.message}</p>
              <X className='h-5 w-5' />
            </div>
          )}

          {isSendSmsSuccess && (
            <div className='flex items-center gap-2 text-green-600'>
              <p>SMS sent successfully!</p>
              <Check className='h-5 w-5' />
            </div>
          )}

          <div className='flex justify-end gap-2'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isSendingSms}>
              {isSendingSms && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              {isSendingSms ? 'Sending...' : 'Send Follow Up'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function StatusDetailsDialog({ message }: { message: SMSMessage }) {
  const [open, setOpen] = useState(false);

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };
  
  const getStatusBadge = () => {
    const status = message.status?.toLowerCase() || 'pending';
    const isWA = message.channel === 'whatsapp';

    switch (status) {
      case 'pending':
        return {
          color: 'bg-status-pending/10 text-status-pending border-status-pending/30',
          icon: <Timer className="h-3 w-3 mr-1" />,
          label: 'Pending'
        };
      case 'sent':
        return {
          color: 'bg-channel-sms/10 text-channel-sms border-channel-sms/30',
          icon: <Check className="h-3 w-3 mr-1" />,
          label: isWA ? 'Sent ✓' : 'Sent'
        };
      case 'delivered':
        return {
          color: 'bg-status-delivered/10 text-status-delivered border-status-delivered/30',
          icon: <CheckCheck className="h-3 w-3 mr-1" />,
          label: isWA ? 'Delivered ✓✓' : 'Delivered'
        };
      case 'read':
        return {
          color: 'bg-status-delivered/10 text-status-delivered border-status-delivered/30',
          icon: <CheckCheck className="h-3 w-3 mr-1" />,
          label: 'Read'
        };
      case 'failed':
        return {
          color: 'bg-status-failed/10 text-status-failed border-status-failed/30',
          icon: <X className="h-3 w-3 mr-1" />,
          label: 'Failed'
        };
      default:
        return {
          color: 'bg-secondary text-ink',
          icon: <Timer className="h-3 w-3 mr-1" />,
          label: status
        };
    }
  };

  const statusBadge = getStatusBadge();
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Badge variant="outline" className={`${statusBadge.color} flex items-center text-xs cursor-pointer`}>
          {statusBadge.icon}
          {statusBadge.label}
        </Badge>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {message.channel === 'whatsapp'
              ? <MessageCircle className="h-5 w-5 text-green-500" />
              : <MessageSquare className="h-5 w-5" />}
            {message.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} Message Status
          </DialogTitle>
          <DialogDescription>
            Detailed delivery information for this message
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="font-medium">Status</div>
            <div className="flex items-center gap-1">
              {statusBadge.icon}
              {statusBadge.label}
            </div>

            {message.channel === 'whatsapp' && (
              <>
                <div className="font-medium">Channel</div>
                <div className="flex items-center gap-1 text-green-600">
                  <MessageCircle className="h-3 w-3" /> WhatsApp
                </div>
              </>
            )}

            <div className="font-medium">Requested At</div>
            <div>{formatTimestamp(message.requestedAt)}</div>

            <div className="font-medium">Sent At</div>
            <div>{formatTimestamp(message.sentAt)}</div>

            <div className="font-medium">Delivered At</div>
            <div>{formatTimestamp(message.deliveredAt)}</div>

            {message.channel === 'whatsapp' && (
              <>
                <div className="font-medium">Read At</div>
                <div>{formatTimestamp(message.readAt)}</div>
              </>
            )}

            {message.status?.toLowerCase() === 'failed' && (
              <>
                <div className="font-medium">Failed At</div>
                <div>{formatTimestamp(message.failedAt)}</div>
                
                {message.errorCode && (
                  <>
                    <div className="font-medium">Error Code</div>
                    <div className="">{message.errorCode}</div>
                  </>
                )}
                
                {message.errorMessage && (
                  <>
                    <div className="font-medium">Error Message</div>
                    <div className="">{message.errorMessage}</div>
                  </>
                )}
                
                {(!message.errorCode && !message.errorMessage && message.error) && (
                  <>
                    <div className="font-medium">Error</div>
                    <div className="text-destructive">{message.error || 'Unknown error'}</div>
                  </>
                )}
              </>
            )}
            
            <div className="font-medium">Recipient</div>
            <div>{message.recipient || (message.recipients && message.recipients[0]) || 'Unknown'}</div>
            
            <div className="font-medium">Message ID</div>
            <div className="font-mono text-xs">{message._id}</div>
            
            {message.smsBatch && (
              <>
                <div className="font-medium">Batch ID</div>
                <div className="font-mono text-xs">{message.smsBatch}</div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MessageCard({ message, type, device }: { message: SMSMessage; type: 'sent' | 'received'; device?: Device }) {
  const isSent = type === 'sent'

  const formattedDate = new Date(
    (isSent ? message.requestedAt : message.receivedAt) || message.createdAt
  ).toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  const isWhatsApp = message.channel === 'whatsapp'
  const shouldShowStatus = isWhatsApp || (device?.appVersionCode && device.appVersionCode >= 14 && new Date(message.createdAt) > new Date('2025-06-05'))

  return (
    <Card
      className={`hover:bg-muted/50 transition-colors ${
        isWhatsApp
          ? 'border-l-4 border-l-channel-wa'
          : isSent
          ? 'border-l-4 border-l-channel-sms'
          : 'border-l-4 border-l-channel-sms'
      }`}
    >
      <CardContent className='p-4'>
        <div className='space-y-3'>
          <div className='flex justify-between items-start'>
            <div className='flex items-center gap-2 flex-wrap'>
              {isWhatsApp && (
                <span className='inline-flex items-center gap-0.5 text-xs font-medium text-channel-wa border border-channel-wa/30 bg-channel-wa/10 rounded px-1.5 py-0.5'>
                  <MessageCircle className='h-3 w-3' />
                  WhatsApp
                </span>
              )}
              {isSent ? (
                <div className='flex items-center text-ink font-medium'>
                  <ArrowUpRight className='h-4 w-4 mr-1' />
                  <span className='font-mono text-sm'>
                    To:{' '}
                    {message.recipient ||
                      (message.recipients && message.recipients[0]) ||
                      'Unknown'}
                  </span>
                </div>
              ) : (
                <div className='flex items-center text-ink font-medium'>
                  <ArrowDownLeft className='h-4 w-4 mr-1' />
                  <span className='font-mono text-sm'>From: {message.sender || 'Unknown'}</span>
                </div>
              )}
            </div>
            <div className='flex items-center gap-1 text-sm text-muted-foreground'>
              <Clock className='h-3 w-3' />
              <span>{formattedDate}</span>
            </div>
          </div>

          <div className='flex gap-2'>
            <p className='text-sm max-w-sm md:max-w-none'>{message.message}</p>
          </div>

          <div className='flex justify-between items-center'>
            {isSent && shouldShowStatus && (
              <div className='flex items-center'>
                <StatusDetailsDialog message={message} />
              </div>
            )}
            
            <div className='flex justify-end ml-auto'>
              {!isSent && <ReplyDialog sms={message} />}
              {isSent && <FollowUpDialog message={message} />}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function MessageCardSkeleton() {
  return (
    <Card className='hover:bg-muted/50 transition-colors max-w-sm md:max-w-none'>
      <CardContent className='p-4'>
        <div className='space-y-3'>
          <div className='flex justify-between items-start'>
            <Skeleton className='h-5 w-24' />
            <Skeleton className='h-4 w-32' />
          </div>
          <Skeleton className='h-4 w-full' />
        </div>
      </CardContent>
    </Card>
  )
}

export default function MessageHistory() {
  const {
    data: devices = [],
    isLoading: isLoadingDevices,
    error: devicesError,
  } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  const [currentDevice, setCurrentDevice] = useState('')
  const [messageType, setMessageType] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (devices?.length && !devices.some((d: Device) => d.id === currentDevice)) {
      setCurrentDevice(devices[0].id)
    }
  }, [devices, currentDevice])

  const {
    data: messagesResponse,
    isLoading: isLoadingMessages,
    error: messagesError,
    refetch,
  } = useQuery({
    queryKey: ['messages-history', currentDevice, messageType, statusFilter, searchQuery, page, limit],
    enabled: !!currentDevice,
    queryFn: () => {
      const params = new URLSearchParams({
        type: messageType,
        page: page.toString(),
        limit: limit.toString(),
      })
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }
      
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim())
      }
      
      return api
        .get(`/gateway/devices/${currentDevice}/messages?${params.toString()}`)
        .then((res) => res.data)
    },
  })

  const handleRefresh = async () => {
    if (!currentDevice) return

    setIsRefreshing(true)
    await refetch()
    setTimeout(() => setIsRefreshing(false), 500)
  }

  useEffect(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current)
      refreshTimerRef.current = null
    }

    if (autoRefreshInterval > 0 && currentDevice) {
      refreshTimerRef.current = setInterval(() => {
        refetch()
        setIsRefreshing(true)
        setTimeout(() => setIsRefreshing(false), 300)
      }, autoRefreshInterval * 1000)
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
      }
    }
  }, [autoRefreshInterval, currentDevice, messageType, statusFilter, searchQuery, page, limit, refetch])

  const messages = messagesResponse?.data?.messages || []

  const pagination = messagesResponse?.data?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  }

  const handleDeviceChange = (deviceId: string) => {
    setCurrentDevice(deviceId)
    setPage(1)
  }

  const handleMessageTypeChange = (type: string) => {
    setMessageType(type)
    setPage(1)
  }

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status)
    setPage(1)
  }

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query)
    setPage(1)
    
    // Debounce the search to avoid too many requests
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      // The query will automatically refetch due to the dependency in useQuery
    }, 300)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  if (isLoadingDevices)
    return (
      <div className='space-y-4'>
        <Skeleton className='h-10 w-full' />
        <div className='space-y-4'>
          {[1, 2, 3].map((i) => (
            <MessageCardSkeleton key={i} />
          ))}
        </div>
      </div>
    )

  if (devicesError)
    return (
      <div className='flex justify-center items-center h-full'>
        Couldn't load your senders. Reload the page to try again.
      </div>
    )

  if (!devices?.length)
    return (
      <div className='flex justify-center items-center h-full'>
        No senders yet. Add one on the Senders page.
      </div>
    )

  return (
    <div className='space-y-4'>
      <div className='bg-card rounded-lg border p-4 mb-4'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col sm:flex-row sm:items-start gap-3'>
            <div className='flex-1'>
              <div className='flex items-center gap-2 mb-1.5'>
                <Smartphone className='h-3.5 w-3.5 text-gray-500' />
                <label htmlFor='history-device' className='text-sm font-medium text-foreground'>Sender</label>
              </div>
              <DevicePicker devices={devices} value={currentDevice} onChange={handleDeviceChange} id='history-device' />
            </div>

            <div className='w-full sm:w-44'>
              <div className='flex items-center gap-2 mb-1.5'>
                <MessageSquare className='h-3.5 w-3.5 text-gray-500' />
                <h3 className='text-sm font-medium text-foreground'>
                  Message Type
                </h3>
              </div>
              <Select
                value={messageType}
                onValueChange={handleMessageTypeChange}
              >
                <SelectTrigger aria-label='Message type' className='w-full h-9 text-sm'>
                  <SelectValue placeholder='Message type' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-gray-500'></div>
                      All Messages
                    </div>
                  </SelectItem>
                  <SelectItem value='received'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-green-500'></div>
                      Received
                    </div>
                  </SelectItem>
                  <SelectItem value='sent'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-status-pending'></div>
                      Sent
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='w-full sm:w-44'>
              <div className='flex items-center gap-2 mb-1.5'>
                <CheckCircle2 className='h-3.5 w-3.5 text-gray-500' />
                <h3 className='text-sm font-medium text-foreground'>
                  Status
                </h3>
              </div>
              <Select
                value={statusFilter}
                onValueChange={handleStatusFilterChange}
              >
                <SelectTrigger aria-label='Status' className='w-full h-9 text-sm'>
                  <SelectValue placeholder='Message status' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-gray-500'></div>
                      All Status
                    </div>
                  </SelectItem>
                  <SelectItem value='pending'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-yellow-500'></div>
                      Pending
                    </div>
                  </SelectItem>
                  <SelectItem value='sent'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-blue-500'></div>
                      Sent
                    </div>
                  </SelectItem>
                  <SelectItem value='delivered'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-green-500'></div>
                      Delivered
                    </div>
                  </SelectItem>
                  <SelectItem value='read'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-channel-sms'></div>
                      Read
                    </div>
                  </SelectItem>
                  <SelectItem value='failed'>
                    <div className='flex items-center gap-1.5'>
                      <div className='h-1.5 w-1.5 rounded-full bg-red-500'></div>
                      Failed
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='flex flex-col gap-3 pt-3'>
            <div className='flex-1'>
              <div className='flex items-center gap-2 mb-1.5'>
                <Search className='h-3.5 w-3.5 text-gray-500' />
                <h3 className='text-sm font-medium text-foreground'>Search Messages</h3>
              </div>
              <Input
                type='text'
                placeholder='Search by message content or recipient...'
                value={searchQuery}
                onChange={(e) => handleSearchQueryChange(e.target.value)}
                className='w-full h-9 text-sm'
              />
            </div>
          </div>

          <div className='flex items-center justify-between gap-2 pt-2 mt-2 border-t'>
            <div className='flex items-center gap-1.5'>
              <Button
                onClick={handleRefresh}
                variant='ghost'
                size='sm'
                disabled={!currentDevice}
                className='h-7 px-2 text-xs text-ink hover:bg-secondary'
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 mr-1 ${
                    isRefreshing ? 'animate-spin' : ''
                  }`}
                />
                Refresh Now
              </Button>
            </div>

            <div className='flex items-center gap-1.5'>
              <Timer className='h-3 w-3 text-gray-500' />
              <span className='text-xs font-medium mr-1'>Auto Refresh:</span>

              <div className='flex'>
                {[
                  { value: 0, label: 'Off' },
                  { value: 15, label: '15s' },
                  { value: 30, label: '30s' },
                  { value: 60, label: '60s' },
                ].map((interval) => (
                  <Button
                    key={interval.value}
                    size='sm'
                    variant='ghost'
                    disabled={!currentDevice && interval.value > 0}
                    className={`h-6 px-1.5 text-xs ${
                      autoRefreshInterval === interval.value
                        ? 'bg-secondary text-ink font-medium'
                        : 'text-muted-foreground hover:bg-secondary'
                    }`}
                    onClick={() => setAutoRefreshInterval(interval.value)}
                  >
                    {interval.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoadingMessages && (
        <div className='space-y-4'>
          {[1, 2, 3].map((i) => (
            <MessageCardSkeleton key={i} />
          ))}
        </div>
      )}

      {messagesError && (
        <div className='flex flex-col items-center gap-3 py-10 text-center'>
          <p className='text-sm text-ink'>Couldn't load messages. Check your connection and try again.</p>
          <Button variant='outline' size='sm' onClick={() => refetch()}>Retry</Button>
        </div>
      )}

      {!isLoadingDevices && !isLoadingMessages && !messagesError && messages.length === 0 && (
        <div className='flex justify-center items-center h-full py-10 text-sm text-gray-600'>
          No messages for this sender yet. Send one from the Send tab.
        </div>
      )}

      <div className='space-y-4'>
        {messages?.map((message: SMSMessage) => (
          <MessageCard
            key={message._id}
            message={message}
            type={message.sender ? 'received' : 'sent'}
            device={devices?.find((device: Device) => device.id === currentDevice)}
          />
        ))}
      </div>

      {pagination.totalPages > 1 && (
        <div className='flex justify-center mt-6 space-x-2'>
          <Button
            onClick={() => handlePageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            variant={page === 1 ? 'ghost' : 'default'}
          >
            Previous
          </Button>

          <div className='flex flex-wrap items-center gap-2 justify-center sm:justify-start'>
            {pagination.totalPages > 1 && (
              <Button
                onClick={() => handlePageChange(1)}
                variant={page === 1 ? 'default' : 'ghost'}
                size='icon'
                aria-label='Page 1'
                className={`h-8 w-8 rounded-full ${
                  page === 1
                    ? 'bg-primary text-amber-foreground hover:bg-primary/90'
                    : 'hover:bg-secondary'
                }`}
              >
                1
              </Button>
            )}

            {page > 4 && pagination.totalPages > 7 && (
              <span className='px-1'>...</span>
            )}

            {Array.from(
              { length: Math.min(6, pagination.totalPages - 2) },
              (_, i) => {
                let pageToShow

                if (pagination.totalPages <= 8) {
                  pageToShow = i + 2
                } else if (page <= 4) {
                  pageToShow = i + 2
                } else if (page >= pagination.totalPages - 3) {
                  pageToShow = pagination.totalPages - 7 + i
                } else {
                  pageToShow = page - 2 + i
                }

                if (pageToShow > 1 && pageToShow < pagination.totalPages) {
                  return (
                    <Button
                      key={pageToShow}
                      onClick={() => handlePageChange(pageToShow)}
                      variant={page === pageToShow ? 'default' : 'ghost'}
                      size='icon'
                      aria-label={`Page ${pageToShow}`}
                      className={`h-8 w-8 rounded-full ${
                        page === pageToShow
                          ? 'bg-primary text-amber-foreground hover:bg-primary/90'
                          : 'hover:bg-secondary'
                      }`}
                    >
                      {pageToShow}
                    </Button>
                  )
                }
                return null
              }
            )}

            {page < pagination.totalPages - 3 && pagination.totalPages > 7 && (
              <span className='px-1'>...</span>
            )}

            {pagination.totalPages > 1 && (
              <Button
                onClick={() => handlePageChange(pagination.totalPages)}
                variant={page === pagination.totalPages ? 'default' : 'ghost'}
                size='icon'
                aria-label={`Page ${pagination.totalPages}`}
                className={`h-8 w-8 rounded-full ${
                  page === pagination.totalPages
                    ? 'bg-primary text-amber-foreground hover:bg-primary/90'
                    : 'hover:bg-secondary'
                }`}
              >
                {pagination.totalPages}
              </Button>
            )}
          </div>

          <Button
            onClick={() =>
              handlePageChange(Math.min(pagination.totalPages, page + 1))
            }
            disabled={page === pagination.totalPages}
            variant={page === pagination.totalPages ? 'ghost' : 'default'}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
